import { useContext, useEffect, useState } from 'react';
import { Navigate, useMatches } from 'react-router';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import { hasPermission, requiresLocation } from '#client/utils/helpers';
import NoPermissions from '#client/pages/NoPermissions';
import { useToast } from '#client/components/common/Toast/ToastContext';
import axios from 'axios';

const ProtectedRoute = ({ children, requiredPermission }) => {
    const matches = useMatches();
    const { isAuthenticated, currentUser, logout, refetchUser, clearInvalidSession, setReturnTo } = useContext(SessionContext);
    const { addToast, clearToasts } = useToast();
    const [has403Error, setHas403Error] = useState(false);

    // Determine if the session is still valid based on expiration time
    const sessionExpires = localStorage.getItem('sessionExpires');
    const isSessionValid = sessionExpires ? new Date(sessionExpires) > new Date() : false;

    // Function to render session expired toast and clear session
    const renderSessionExpiredToast = () => {
        clearToasts();
        addToast({
            type: 'warning',
            message: 'Your session has expired. Please log in again.',
        });
        clearInvalidSession();
    };

    // Global axios interceptor for session expiration
    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                // Check for 401 Unauthorized
                if (error.response?.status === 401) {
                    // Get the current url and redirect to login with returnTo
                    setReturnTo();
                    renderSessionExpiredToast();
                }
                // Check for 403 Forbidden. show <NoPermissions permission="missing_role" />;
                if (error.response?.status === 403) {
                    setHas403Error(true);
                }

                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, []);

    // Route-level session refresh
    useEffect(() => {
        // Check if any matched route has refreshSession in its handle
        const shouldRefresh = matches.some((match) => match.handle?.refreshSession);

        if (shouldRefresh && isAuthenticated && refetchUser) {
            (async () => {
                await refetchUser();
            })();
        }
    }, [matches, isAuthenticated, refetchUser, logout]);

    // Clear session if expired
    useEffect(() => {
        if (!isSessionValid) {
            renderSessionExpiredToast();
        }
    }, [isSessionValid, clearInvalidSession]);

    // Show NoPermissions if 403 error occurred
    if (has403Error) {
        return <NoPermissions permission="missing_role" />;
    }

    // Clear session if expired
    if (!isSessionValid) {
        // Get the current url and redirect to login with returnTo
        setReturnTo();

        return <Navigate to="/" replace />;
    }

    // Redirect if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    // If no role, redirect to NoPermissions page
    if (currentUser && (currentUser?.role?.roleName === 'None' || currentUser?.role?.roleName === null)) {
        return <NoPermissions permission="role" />;
    }

    // Does route require a location-based permission?
    if (requiredPermission && !requiresLocation(currentUser, requiredPermission)) {
        return <NoPermissions permission="location" />;
    }

    // Redirect if insufficient permissions
    if (requiredPermission && !hasPermission(currentUser, requiredPermission)) {
        return <NoPermissions permission="missing_role" />;
    }

    return children;
};

export default ProtectedRoute;
